import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import cx from "classnames";
import IBuildrProps from '../../../utilities/IBuildrProps';
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../../utilities/automation';
import InputMask from "react-input-mask";
import CurrencyInput from 'react-currency-input-field';

export interface IInputTextProps extends IBuildrProps {
    inputLabel: string;
    inputDefaultValue: string;
    inputFieldName?: string;
    inputRequired?: boolean;
    hintText?: string;
    onChange?: Function;
    className?: string;
    inputType?: string;
    underline?: boolean;
    dataTestId?: string;
    onKeyDown?: (value) => void;
    inputRef?: any;
}

export default styled(forwardRef(function InputText(props: IInputTextProps, ref: any) {
    const { inputLabel, inputDefaultValue, inputFieldName, inputRequired, hintText, className, cmsId, editorClassObj, inputType = 'text', onChange = () => {}, underline = false, inputRef, dataTestId, onKeyDown } = props;
    const [value, setValue] = useState(inputDefaultValue);
    const [unmaskedValue, setUnmaskedValue] = useState(inputDefaultValue);
    const id = useId();
    const inputReference = useRef(null);

    useEffect(() => {
        setValue(inputDefaultValue || '');
    }, [inputDefaultValue]);

    useEffect(() => {
        if (value && inputType === 'tel') {
            setUnmaskedValue(value.replace(/[^\d]/g, ''));
        }
        if (value && inputType === 'currency') {
            onChange(typeof value === 'string' ? value?.replace(/-/g, '') : value);
        } else {
            onChange(value);
        }
    }, [value]);

    function renderInput() {
        if (inputType === 'tel') {
            return (<>
                <InputMask
                    id={id}
                    ref={inputRef || inputReference}
                    class={cx("form-control", {"placeholder-shown": !unmaskedValue}, {'px-0': underline})}
                    mask="(999) 999-9999"
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    value={value}
                    type={inputType}
                    inputmode="numeric"
                    placeholder={inputLabel}
                    title={inputLabel}
                    name={inputFieldName+"Masked"}
                    required={inputRequired}
                    data-test-id={dataTestId || createDataTestId({ feature: AutomationTestIdFeature.FORM, element: AutomationTestIdElementType.INPUT, specifier: inputType })} />
                <input
                    type="hidden"
                    name={inputFieldName}
                    value={'1'+ unmaskedValue || ''} // Adding default '1' to unmasked number
                />
            </>);
        } else if (inputType === 'currency') {
            return (
                <CurrencyInput
                    id={id}
                    min={0}
                    ref={inputRef || inputReference}
                    className={cx("form-control", {"placeholder-shown": !value}, {'px-0': underline})}
                    type={inputType}
                    placeholder={inputLabel}
                    title={inputLabel}
                    name={inputFieldName}
                    required={inputRequired}
                    step={1}
                    value={value}
                    onValueChange={value => setValue(value?.replace(/-/g, ''))}
                    onKeyDown={onKeyDown}
                    intlConfig={{ locale: 'en-US', currency: 'USD' }}
                    data-test-id={dataTestId || createDataTestId({ feature: AutomationTestIdFeature.FORM, element: AutomationTestIdElementType.INPUT, specifier: inputType })} />
            );
        } else {
            return (
                <input
                    id={id}
                    ref={inputRef || inputReference}
                    className={cx("form-control", {"placeholder-shown": !value}, {'px-0': underline})}
                    type={inputType}
                    placeholder={inputLabel}
                    title={inputLabel}
                    name={inputFieldName}
                    required={inputRequired}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    data-test-id={dataTestId || createDataTestId({ feature: AutomationTestIdFeature.FORM, element: AutomationTestIdElementType.INPUT, specifier: inputType })} />
            );
        }
    }

    return (
        <div data-id={cmsId} ref={ref} className={cx('input-text mb-3', className, editorClassObj, {'underline px-0': underline})}>
            {renderInput()}
            <label htmlFor={id} className={cx({'px-0': underline})}>{inputLabel}</label>
            {hintText ? <div><small>{hintText}</small></div> : null}
        </div>
    )
}))`
    position: relative;

    &.underline {
        input, input:focus {
            background: transparent;
            border-bottom: 1px solid rgb(33, 33, 33) !important;
            border-radius: 0;
            font-weight: 500;
        }
        label {
            font-weight: 500;
        }
    }

    & > input,
    & > label {
        padding: var(--input-padding-y) 0;
        height: 54px;
        font-weight: 500;
    }

    & > label {
        position: absolute;
        top: 0;
        display: block;
        width: 100%;
        margin-bottom: 0; /* Override default <label> margin */
        color: var(--mud, #5E514D);
        opacity: 0.5;
        transition: all .1s ease-in-out;
        pointer-events: none;
        font-size: 0.889rem;
        line-height: 1.5;
    }

    input::placeholder {
        color: transparent;
    }

    .form-control {
        border: 0;
    }

    .form-control:not(:placeholder-shown), .form-control:focus {
        padding-top: calc(var(--input-padding-y) + var(--input-padding-y) * (2 / 3));
        padding-bottom: calc(var(--input-padding-y) / 3);
    }

    .form-control:not(:placeholder-shown) ~ label, .form-control:focus ~ label {
        padding-top: calc(var(--input-padding-y) / 3);
        padding-bottom: calc(var(--input-padding-y) / 3);
        font-size: 0.556rem;
        color: var(--mud, #5E514D);
        opacity: 1;
    }
`;
